/**
 * Constants shared between frontend and backend
 * Color maps, feature tiers, and configuration limits
 */
// ── Signal Type Colors ────────────────────────────────────
/**
 * Deterministic color mapping for signal types
 * Used to visually distinguish different signal flows
 */
export const SIGNAL_TYPE_COLORS = {
    audio: '#3B82F6', // blue
    midi: '#10B981', // green
    sync: '#F59E0B', // amber
    cv_gate: '#8B5CF6', // purple
    adat: '#06B6D4', // cyan
    word_clock: '#14B8A6', // teal
    usb: '#6B7280', // gray
    ethernet: '#78716C', // stone
};
/**
 * Human-readable labels for signal types
 */
export const SIGNAL_TYPE_LABELS = {
    audio: 'Audio',
    midi: 'MIDI',
    sync: 'Sync',
    cv_gate: 'CV/Gate',
    adat: 'ADAT',
    word_clock: 'Word Clock',
    usb: 'USB',
    ethernet: 'Ethernet',
};
/**
 * Get color for a signal type
 * @param signalType - The signal type to get color for
 * @returns Hex color code
 */
export function getColorForSignalType(signalType) {
    return SIGNAL_TYPE_COLORS[signalType] || '#999999';
}
// ── Device Category Colors ───────────────────────────────
/**
 * Color mapping for device categories
 * Used to visually group device types
 */
export const DEVICE_CATEGORY_COLORS = {
    interface: '#EC4899', // pink
    mixer: '#8B5CF6', // purple
    synth: '#06B6D4', // cyan
    drum_machine: '#EF4444', // red
    sampler: '#F97316', // orange
    controller: '#6366F1', // indigo
    effects: '#A78BFA', // light purple
    monitoring: '#3B82F6', // blue
    midi_interface: '#10B981', // green
    groovebox: '#F59E0B', // amber
    semi_modular: '#14B8A6', // teal
    computer: '#64748B', // slate
    usb_hub: '#6B7280', // gray
    network_switch: '#78716C', // stone
    recorder: '#DC2626', // dark red
    microphone: '#F43F5E', // rose
};
/**
 * Human-readable labels for device categories
 */
export const DEVICE_CATEGORY_LABELS = {
    interface: 'Audio Interface',
    mixer: 'Mixer',
    synth: 'Synthesizer',
    drum_machine: 'Drum Machine',
    sampler: 'Sampler',
    controller: 'Controller',
    effects: 'Effects Processor',
    monitoring: 'Monitoring/Speaker',
    midi_interface: 'MIDI Interface',
    groovebox: 'Groovebox',
    semi_modular: 'Semi-Modular Synth',
    computer: 'Computer',
    usb_hub: 'USB Hub',
    network_switch: 'Network Switch',
    recorder: 'Recorder',
    microphone: 'Microphone',
};
/**
 * Get color for a device category
 * @param category - The device category to get color for
 * @returns Hex color code
 */
export function getColorForDeviceCategory(category) {
    return DEVICE_CATEGORY_COLORS[category] || '#999999';
}
// ── Legacy Color Palette ──────────────────────────────────
/**
 * Legacy random color palette for custom devices/zones
 * Kept for backward compatibility
 */
export const COLOR_PALETTE = [
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#FFA07A',
    '#98D8C8',
    '#F06292',
    '#BA68C8',
    '#FFD54F',
    '#4DB6AC',
    '#7986CB',
];
// ── Subscription Tier Features ────────────────────────────
/**
 * Feature limits and capabilities for each subscription tier
 */
export const TIER_FEATURES = {
    free: {
        maxLayouts: 1,
        maxDevicesPerLayout: 20,
        maxZonesPerLayout: 2,
        aiSuggestions: false,
        cloudSync: false,
        teamSharing: true,
        maxActiveShares: 3,
        exportFormats: ['json'],
        maxMidiRoutes: 1,
        maxSequencerProjects: 1,
        sequencerKits: 'basic',
        sequencerSave: false,
        maxSequencerMidiPads: 0,
        sampleSlicer: false,
    },
    // Home Platform + Pro Platform both map to `pro` at the backend
    // (the Home/Pro dimension is a licensing/use-context line, not a
    // feature line). The bundle launch promised "unlimited layouts
    // and MIDI routes" — these limits reflect that promise. Global
    // safety caps (MAX_LAYOUT_DEVICES etc. below) still apply to
    // prevent a single layout from exploding cloud costs.
    pro: {
        maxLayouts: Number.POSITIVE_INFINITY,
        maxDevicesPerLayout: 500,
        maxZonesPerLayout: 100,
        aiSuggestions: true,
        cloudSync: true,
        teamSharing: true,
        maxActiveShares: 25,
        exportFormats: ['json', 'pdf', 'svg'],
        maxMidiRoutes: Number.POSITIVE_INFINITY,
        maxSequencerProjects: 16,
        sequencerKits: 'all',
        sequencerSave: true,
        maxSequencerMidiPads: 16,
        sampleSlicer: true,
    },
    studio: {
        maxLayouts: Number.POSITIVE_INFINITY,
        maxDevicesPerLayout: 500,
        maxZonesPerLayout: 100,
        aiSuggestions: true,
        cloudSync: true,
        teamSharing: true,
        maxActiveShares: Number.POSITIVE_INFINITY,
        exportFormats: ['json', 'pdf', 'svg', 'png', 'html'],
        maxMidiRoutes: Number.POSITIVE_INFINITY,
        maxSequencerProjects: 16,
        sequencerKits: 'all',
        sequencerSave: true,
        maxSequencerMidiPads: 16,
        sampleSlicer: true,
    },
};
// ── Layout Limits ─────────────────────────────────────────
/**
 * Global maximum values for layout components
 */
export const MAX_LAYOUT_DEVICES = 500;
export const MAX_LAYOUT_CONNECTIONS = 2000;
export const MAX_LAYOUT_ZONES = 100;
/**
 * Canvas/viewport bounds
 */
export const CANVAS_MIN_ZOOM = 0.1;
export const CANVAS_MAX_ZOOM = 5;
export const CANVAS_WIDTH = 8000;
export const CANVAS_HEIGHT = 6000;
// ── Device Limits ─────────────────────────────────────────
/**
 * Maximum number of ports per device
 */
export const MAX_PORTS_PER_DEVICE = 100;
/**
 * Maximum string lengths for various fields
 */
export const MAX_FIELD_LENGTHS = {
    deviceName: 255,
    layoutName: 255,
    zoneName: 255,
    portName: 100,
    description: 2000,
    notes: 500,
};
// ── Grid Settings ─────────────────────────────────────────
/**
 * Default grid size for devices (in pixels)
 */
export const DEFAULT_GRID_SIZE = 4;
/**
 * Snap-to-grid step size
 */
export const GRID_STEP = 16;
// ── Animation/UI Timings ──────────────────────────────────
/**
 * Default animation duration for transitions (ms)
 */
export const ANIMATION_DURATION = 300;
/**
 * Debounce delay for layout saves (ms)
 */
export const SAVE_DEBOUNCE_DELAY = 1000;
/**
 * Timeout for AI suggestion requests (ms)
 */
export const AI_REQUEST_TIMEOUT = 30000;
// ── Batch Operation Limits ────────────────────────────────
/**
 * Maximum number of operations per batch write request
 */
export const MAX_BATCH_OPERATIONS = 100;
/**
 * Maximum number of operations to process concurrently
 */
export const BATCH_CONCURRENCY = 10;
// ── AI Configuration ──────────────────────────────────────
/**
 * Default confidence threshold for accepting AI suggestions
 */
export const DEFAULT_AI_CONFIDENCE_THRESHOLD = 0.7;
/**
 * Maximum number of AI suggestions to return per request
 */
export const MAX_AI_SUGGESTIONS = 50;
// ── Device Presets ────────────────────────────────────────
/**
 * Device preset library for quick device addition
 */
export { DEVICE_PRESETS } from './devicePresets.js';
/**
 * Werkshop module catalog — first-party plugin list the Store
 * renders, the pad picker gates, and the checkout flow derives
 * Stripe price IDs from.
 */
export { PLUGIN_CATALOG, FREE_PLUGIN_IDS, getPluginCatalogEntry, listPluginsByCategory, } from './plugins.js';
//# sourceMappingURL=index.js.map